import { EnvironmentService } from 'app/shared/services/environment-service';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

export type CharacterType = keyof typeof CHARACTER_TYPES;
export type Rule = keyof typeof RULES;
export type PasswordValidationResult = {
    isValid: boolean;
    errors: string[];
}

const CHARACTER_TYPES = {
    letter: /[a-zA-Z]/,
    upperCase: /[A-Z]/,
    lowerCase: /[a-z]/,
    number: /[0-9]/,
    symbol: /[^A-Za-z0-9 \\s]/
};
const RULES = {
    minLength: (param: number) => (password: string): boolean => {
        return new RegExp(`^.{${param},}$`).test(password);
    },
    maxLength: (param: number) => (password: string): boolean => {
        return new RegExp(`^.{0,${param}}$`).test(password);
    },
    characterTypes: (param: CharacterType[], options: { minCharacterTypes?: number }) => (password: string): boolean => {
        let minCharacterTypes = options.minCharacterTypes || param.length;
        return param.some((type: CharacterType) => CHARACTER_TYPES[type].test(password) && --minCharacterTypes == 0);
    },
    minKeywords: (param: number) => (password: string): boolean => {
        return new RegExp(`^(?:\\W*\\b\\S+\\b\\W*){${param},}$`).test(password);
    },
    minKeywordLength: (param: number) => (password: string): boolean => {
        return new RegExp(`^(?:\\W*\\b\\S{${param},}\\b\\W*)+$`).test(password);
    }
}

@autoinject()
export class PasswordService {

    constructor(protected readonly env: EnvironmentService, protected readonly i18n: I18N) { }

    public validatePassword(password: string): PasswordValidationResult {
        const rules = this.env.config.passwordRules || {};
        const errors = [] as string[];
        const options = {
            minCharacterTypes: rules['minCharacterTypes']
        };
        let isValid = true;
        for(let rule in rules) {
            if(rule === 'custom') {
                for(let { pattern, error } of rules[rule]!) {
                    if(!new RegExp(pattern).test(password)) {
                        isValid = false;
                        errors.push(error);
                    }
                }
            }

            // INFO [Matt] minCharacterTypes is an additional option for characterTypes.
            else if(rule !== 'minCharacterTypes') {
                const param = rules[rule as Rule] as any;
                const test = RULES[rule as Rule](param, options);
                if(!test(password)) {
                    isValid = false;
                    let errorKey = rule;
                    let errorParam = param;
                    if(rule === 'characterTypes') {
                        if(options.minCharacterTypes !== undefined) {
                            errorKey = 'minCharacterTypes';
                        }
                        const translatedParams = param.map((type: string) => this.i18n.tr(`ui:validation.character.${type}`));

                        // INFO [Matt] Adds commas and an "and", including an oxford comma.
                        errorParam = `${translatedParams.slice(0, param.length - 1)}, and ${translatedParams[param.length - 1]}`;

                        // INFO [Matt] Capitalizes the first letter after the colon.
                        errorParam = `${errorParam[0].toUpperCase()}${errorParam.slice(1)}`;
                    }
                    errors.push(this.i18n.tr(`ui:validation.${errorKey}`, { what: 'Password', param: errorParam, options }));
                }
            }
        }
        return { isValid, errors };
    }

    public getPasswordRequirements(): string[] {
        const rules = this.env.config.passwordRules || {};
        const passwordRequirements = [] as string[];
        const options = {
            minCharacterTypes: rules['minCharacterTypes']
        };
        for(let rule in rules) {
            if(rule === 'custom') {
                for(let { error } of rules[rule]!) {
                    passwordRequirements.push(error);
                }
            }

            // INFO [Matt] minCharacterTypes is an additional option for characterTypes.
            else if(rule !== 'minCharacterTypes') {
                const param = rules[rule as Rule] as any;
                let errorKey = rule;
                let errorParam = param;
                if(rule === 'characterTypes') {
                    if(options.minCharacterTypes !== undefined) {
                        errorKey = 'minCharacterTypes';
                    }
                    const translatedParams = param.map((type: string) => this.i18n.tr(`ui:validation.character.${type}`));

                    // INFO [Matt] Adds commas and an "and", including an oxford comma.
                    errorParam = `${translatedParams.slice(0, param.length - 1)}, and ${translatedParams[param.length - 1]}`;

                    // INFO [Matt] Capitalizes the first letter after the colon.
                    errorParam = `${errorParam[0].toUpperCase()}${errorParam.slice(1)}`;
                }
                passwordRequirements.push(this.i18n.tr(`ui:validation.${errorKey}`, { what: 'Password', param: errorParam, options }));
            }
        }
        return passwordRequirements;
    }
}
